import {
  Avatar as AvatarImage,
  Button,
  Checkbox,
  Input,
  List,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../../app/store";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import Avatar from "../../../../../assets/images/avatar.svg";
import ActionModal from "../../../../../components/ui/ActionModal";
import CustomModal from "../../../../../components/ui/CustomModal";
import Loader from "../../../../../components/ui/Loader";
import { useGetAllDepartmentQuery } from "../../../../../features/superAdmin/employeeService/employeeManagementService";
import {
  useGetChatUserListQuery,
  useRaiseTicketMutation,
} from "../../../../../features/superAdmin/ticketService/ticketServiceApi";
import useDebounce from "../../../../../hooks/useDebounce";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import { ModalComponentProps } from "../../../../../types";
import { generatePath } from "../../../../../utils/utils";

export default function AddCCModal({
  handleClose,
  data,
  templateId,
  handleParentClose,
  ...rest
}: ModalComponentProps & {
  data: any;
  templateId?: number;
  handleParentClose?: () => void;
}) {
  const { user } = useAppSelector((state) => state.auth);
  const {
    open,
    handleOpen,
    handleClose: closeModal,
  } = useModal<unknown, "CONFIRM">();
  const { id } = useParams();
  const [selected, setSelected] = useState<number[]>([]);
  const [dept, setDept] = useState<number[]>([]);
  const { keyword, handleInputChange } = useDebounce();

  const [
    raiseTicket,
    {
      data: raiseTicketData,
      isLoading: raiseTicketIsLoading,
      isError: raiseTicketIsError,
      error: raiseTicketError,
      isSuccess: raiseTicketIsSuccess,
    },
  ] = useRaiseTicketMutation();

  const {
    isLoading,
    isError,
    error,
    data: allEmployeesData,
  } = useGetChatUserListQuery({
    params: {
      search: keyword,
      dept_ids: dept ?? [],
    },
  });
  const {
    isError: isDeptError,
    error: deptError,
    data: deptData,
    isLoading: deptLoading,
  } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100000000,
      status: "active",
    },
  });

  useError(isDeptError, deptError, "Get all department list fail");
  useError(isError, error, "Get all user list fail");
  useError(raiseTicketIsError, raiseTicketError, "Raise ticket create fail");

  const handleAddCC = () => {
    const ccdata = {
      ...data,
      cc_list: selected?.map((item) => ({
        employee_id: item,
      })),
    };
    raiseTicket({ id: templateId || id, data: ccdata });
  };

  const employeeData = allEmployeesData?.data;

  const handleChecked = (id: number, checked: boolean) => {
    if (checked) {
      setSelected((prev) => [...prev, id]);
    } else {
      setSelected((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    const employeeIds = employeeData?.map((item) => item?.id);

    if (checked && employeeIds?.length) {
      setSelected(employeeIds);
    } else {
      setSelected([]);
    }
  };
  const navigate = useNavigate();
  useEffect(() => {
    if (raiseTicketIsSuccess) {
      // handleClose();
      handleOpen({ type: "VIEW" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raiseTicketIsSuccess]);

  return (
    <CustomModal
      {...rest}
      handleClose={handleClose}
      width={400}
      footer={null}
      closeIcon={false}
      title="Add to CC (Optional)"
    >
      {open.type === "VIEW" && (
        <ActionModal
          title={`Ticket Created Successfully`}
          subTitle="Your ticket has been created successfully."
          open={open?.open}
          iconType="success"
          cancelButton={false}
          actionBtnType="bordered-light"
          actionBtnText="View Ticket"
          handleClose={() => {
            handleClose();
            closeModal();
            handleParentClose && handleParentClose();
          }}
          handleAction={() => {
            navigate(
              generatePath(
                user?.role as string,
                `ticket-management/all-tickets/creator-details`,
                raiseTicketData?.data?.generated_ticket_log_id,
              ),
            );
          }}
        />
      )}
      <div>
        <Input
          size="large"
          prefix={<SearchIcon />}
          placeholder="Search"
          onChange={(e) => {
            handleInputChange(e);
            setSelected([]);
          }}
        />
        <div className="mt-3 flex gap-3">
          <Select
            showSearch
            allowClear
            size="large"
            mode="multiple"
            placeholder="Department"
            optionFilterProp="label"
            disabled={deptLoading}
            options={deptData?.data.map((dep: any) => ({
              label: dep.name,
              value: dep.id,
            }))}
            value={dept}
            onChange={(e) => {
              setDept(e);
              setSelected([]);
            }}
            className="flex-1"
          />
        </div>
        <div
          className={`flex justify-between items-center mt-2 py-2 pr-2 shadow-sm ${
            (employeeData?.length as number) > 6 && "mr-2"
          }`}
        >
          <span>Select all</span>
          <span>
            <Checkbox
              disabled={employeeData?.length === 0 || isLoading}
              checked={employeeData?.length === selected.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
          </span>
        </div>
        <div className="h-[300px] overflow-auto pr-2 pt-2">
          {isLoading ? (
            <Loader />
          ) : (
            <List
              dataSource={employeeData}
              renderItem={(item) => {
                const isChecked = selected.includes(item.id);

                return (
                  <List.Item
                    onClick={() => handleChecked(item.id, !isChecked)}
                    className="!border-none !py-1.5 cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex gap-4 items-center">
                        <AvatarImage
                          src={(item.personalInfo?.pro_pic || Avatar) as string}
                          size={40}
                        />
                        <span className="text-[17px]">
                          {item.officialInfo?.name}
                        </span>
                      </div>
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) =>
                          handleChecked(item.id, e.target.checked)
                        }
                      />
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
        </div>
        <div className="py-2 flex justify-between">
          <Button className="button-bordered-light h-10" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            loading={raiseTicketIsLoading}
            className="button-primary-light h-10"
            onClick={handleAddCC}
          >
            Create
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
